import {FC, useState, createContext, useContext, useEffect, useMemo} from 'react'
import {
  QueryState,
  QueryRequestContextProps,
  initialQueryRequest,
  WithChildren,
} from '../../../../_metronic/helpers'
import {useLocation, useSearchParams} from 'react-router-dom'

const QueryRequestContext = createContext<QueryRequestContextProps>(initialQueryRequest)

type DeliveryDeepLink = {
  status: string | null
  punctuality: string | null
  endFrom: string | null
  endTo: string | null
}

const buildInitialFilter = (
  pathname: string,
  mode: string,
  priorityParam: string | null,
  overdueParam: string | null,
  delivery: DeliveryDeepLink
) => {
  const baseFilter: any = {
    status: 'aberta',
    mode,
    sort_by_delivery_date: true,
  }

  const isInstanceTasksRoute = pathname.startsWith('/tarefas')
  const isOverdue = overdueParam === 'true' || overdueParam === '1'

  // Deep-links do dashboard do colaborador para /mytasks:
  // - ?overdue=true            (card "Tarefas atrasadas" do topo)
  // - ?status=fechada&punctuality=on_time|late&endFrom=&endTo=  (cards de entrega)
  if (!isInstanceTasksRoute) {
    if (isOverdue) {
      return {...baseFilter, only_overdue_tasks: true}
    }

    const punctuality =
      delivery.punctuality === 'on_time' || delivery.punctuality === 'late'
        ? delivery.punctuality
        : null

    if (punctuality || delivery.status === 'fechada') {
      const filter: any = {
        ...baseFilter,
        status: 'fechada',
      }
      if (punctuality) {
        filter.delivery_punctuality = punctuality
      }
      if (delivery.endFrom || delivery.endTo) {
        filter.end_date = {
          begin_date: delivery.endFrom ?? '',
          end_date: delivery.endTo ?? '',
        }
      }
      return filter
    }

    return baseFilter
  }

  if (isOverdue) {
    return {
      ...baseFilter,
      only_overdue_tasks: true,
    }
  }

  if (priorityParam) {
    const normalizedPriority = priorityParam.trim().toLowerCase()
    const priorityMap: Record<string, string> = {
      critica: 'critical',
    }
    const mappedPriority = priorityMap[normalizedPriority]
    return {
      ...baseFilter,
      priority: [mappedPriority ?? normalizedPriority],
    }
  }

  return baseFilter
}

const QueryRequestProvider: FC<WithChildren> = ({children}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const mode = searchParams.get('mode') ?? 'table'
  const priorityParam = searchParams.get('priority')
  const overdueParam = searchParams.get('overdue')
  const statusParam = searchParams.get('status')
  const punctualityParam = searchParams.get('punctuality')
  const endFromParam = searchParams.get('endFrom')
  const endToParam = searchParams.get('endTo')
  const deliveryDeepLink: DeliveryDeepLink = useMemo(
    () => ({
      status: statusParam,
      punctuality: punctualityParam,
      endFrom: endFromParam,
      endTo: endToParam,
    }),
    [statusParam, punctualityParam, endFromParam, endToParam]
  )
  const [state, setState] = useState<QueryState>({
    ...initialQueryRequest.state,
    filter: buildInitialFilter(location.pathname, mode, priorityParam, overdueParam, deliveryDeepLink),
  })

  const updateState = (updates: Partial<QueryState>) => {
    const updatedState = {...state, ...updates} as QueryState
    setState(updatedState)
  }

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams)
    let shouldUpdateParams = false

    if (!searchParams.get('mode')) {
      newParams.set('mode', 'table')
      shouldUpdateParams = true
    }

    if (location.pathname.startsWith('/tarefas')) {
      const normalizedPriority = priorityParam?.trim().toLowerCase()
      const validPriorities = ['critical', 'critica', 'high', 'alta', 'medium', 'media', 'low', 'baixa']
      if (priorityParam && !validPriorities.includes(normalizedPriority || '')) {
        newParams.delete('priority')
        shouldUpdateParams = true
      }

      const validOverdue = overdueParam === null || overdueParam === 'true' || overdueParam === '1'
      if (!validOverdue) {
        newParams.delete('overdue')
        shouldUpdateParams = true
      }
    }

    if (shouldUpdateParams) {
      setSearchParams(newParams)
    }

    const nextFilter = buildInitialFilter(
      location.pathname,
      mode,
      priorityParam,
      overdueParam,
      deliveryDeepLink
    )
    setState((prev) => {
      const prevFilter = (prev.filter ?? {}) as any
      const shouldSyncFilter =
        prevFilter.mode !== nextFilter.mode ||
        prevFilter.status !== nextFilter.status ||
        prevFilter.only_overdue_tasks !== nextFilter.only_overdue_tasks ||
        (prevFilter.delivery_punctuality ?? null) !== (nextFilter.delivery_punctuality ?? null) ||
        JSON.stringify(prevFilter.priority ?? []) !== JSON.stringify(nextFilter.priority ?? [])

      if (!shouldSyncFilter) return prev

      // Mescla em vez de substituir: nextFilter só é autoritativo pros campos vindos de
      // deep-link/mode (status, only_overdue_tasks, delivery_punctuality, priority, mode).
      // Substituir o filtro inteiro aqui apagava qualquer filtro aplicado manualmente pelo
      // TaskFilterBar (tags, cliente/projeto, subtarefas, responsáveis, datas) sempre que o
      // usuário alternava entre lista e kanban, já que isso muda `mode` e reexecuta este efeito.
      return {
        ...prev,
        filter: {...prevFilter, ...nextFilter},
      }
    })
  }, [location.pathname, mode, priorityParam, overdueParam, deliveryDeepLink])

  return (
    <QueryRequestContext.Provider value={{state, updateState}}>
      {children}
    </QueryRequestContext.Provider>
  )
}

const useQueryRequest = () => useContext(QueryRequestContext)
export {QueryRequestProvider, useQueryRequest}
